import { GridRowParams } from '@mui/x-data-grid-pro';
import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import {
  SmallTableEmptyState,
  SmallTableEmptyStateFill,
} from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Wallet01Icon } from '@/components/icons/Wallet01Icon';
import { MAXIMUM_ROW_COUNT } from '@/components/tables/DataTable/constants';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { ActionButtonRenderer } from '@/components/tables/DataTable/renderers/cell/ActionButtonRenderer';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column, DataTableProps } from '@/components/tables/DataTable/types';
import { useModalState } from '@/hooks/useModalState';
import { IntegrationLinkedIcon } from '@/modules/assetProviderIntegrations/shared/IntegrationLinkedIcon';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { CashflowDrawer } from '@/modules/cashflows/CashflowDrawer/CashflowDrawer';
import { Cashflows_CashflowFragment } from '@/modules/cashflows/CashflowDrawer/graphql/Cashflows.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';
import { formatPercentNoDecimals } from '@/utils/formatting/percent';

import { DeleteCashflowMenuItem } from '../DeleteCashflowMenuItem/DeleteCashflowMenuItem';
import { DuplicateCashflowMenuItem } from '../DuplicateCashflowMenuItem/DuplicateCashflowMenuItem';
import { mapDataToRows } from './CashflowTable.utils';

export interface CashflowTableProps {
  tableProps?: Partial<DataTableProps>;
  cashflows: Cashflows_CashflowFragment[];
  entityId?: string;
  individualId?: string;
  refetch: () => void;
  /**
   * How the empty state fills vertical space. Only set to `'viewport'` on
   * full-page usages; leave unset in height-constrained containers (e.g. the
   * estate waterfall side panel) so the empty state doesn't balloon.
   */
  emptyStateFill?: SmallTableEmptyStateFill;
}

export interface CashflowTableRowData {
  id: string;
  name: string;
  amount: Decimal | null;
  transferPercentage: Decimal | null;
  description: string;
  entityName: string;
  growthRate: Decimal | null;
  isIntegrationLinked: boolean;
  actions?: null; // Added for the actions column
}

const getColumns = (
  includeEntity: boolean,
  refetch: () => void,
  canEdit: boolean
): Column<CashflowTableRowData>[] =>
  compact([
    {
      field: 'name',
      headerName: 'Cash flow',
      width: 200,
      flex: 1,
      renderCell: TwoLineTextRenderer({
        lineOne: ({ row }) => row.name,
        lineTwo: ({ row }) => row.description,
        textAccompaniment: ({ row }) =>
          row.isIntegrationLinked ? <IntegrationLinkedIcon /> : null,
      }),
    },
    includeEntity && {
      field: 'entityName',
      headerName: 'Client/entity',
      width: 200,
      flex: 1,
      renderCell: TextRenderer({
        text: ({ row }) => row.entityName,
      }),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 250,
      flex: 1,
      align: 'right',
      renderCell: TwoLineTextRenderer({
        lineOne: ({ row }) => {
          // Display percentage if transferPercentage exists, otherwise display currency amount
          if (row.transferPercentage) {
            return row.transferPercentage.isNegative()
              ? `(${formatPercentNoDecimals(row.transferPercentage.abs())}%)`
              : `${formatPercentNoDecimals(row.transferPercentage)}%`;
          }
          return formatCurrencyNoDecimalsAccounting(
            row.amount ?? new Decimal(0)
          );
        },
        lineTwo: ({ row }) =>
          row.growthRate?.gt(0) ? `${row.growthRate.toString()}% growth` : null,
        lineOneProps: ({ row }) => ({
          color:
            row.amount?.isNegative() || row.transferPercentage?.isNegative()
              ? COLORS.FUNCTIONAL.ERROR[600]
              : COLORS.TEXT.PRIMARY,
        }),
      }),
    },
    canEdit && {
      field: 'actions',
      headerName: '',
      width: 64,
      minWidth: 64,
      maxWidth: 64,
      sortable: false,
      align: 'center',
      renderCell: ActionButtonRenderer({
        actions: () => [
          {
            MenuItem: (rowData) => (
              <DuplicateCashflowMenuItem
                cashflowId={rowData.id}
                onDuplicate={refetch}
              />
            ),
          },
          {
            MenuItem: (rowData) => (
              <DeleteCashflowMenuItem
                cashflowId={rowData.id}
                onDelete={refetch}
              />
            ),
          },
        ],
      }),
    },
  ]);

export function CashflowsTable({
  cashflows,
  tableProps: paginatedTableProps = {},
  entityId,
  individualId,
  refetch,
  emptyStateFill,
}: CashflowTableProps) {
  const canEdit = useCanEditHouseholdData();
  const { isTwoClientHousehold } = useHouseholdDetailsContext();
  const [isAddCashflowDrawerOpen, setIsAddCashflowDrawerOpen] = useState(false);
  const [{ isModalOpen, data: cashflowId }, { closeModal, openModal }] =
    useModalState<string>();
  const rows = useMemo(
    () => mapDataToRows(cashflows, isTwoClientHousehold),
    [cashflows, isTwoClientHousehold]
  );
  const hasRecipient = !!individualId || !!entityId;

  const onRowClick = useCallback(
    ({ row }: GridRowParams<CashflowTableRowData>) => {
      openModal(row.id);
    },
    [openModal]
  );

  const columns = useMemo(
    () => getColumns(!hasRecipient, refetch, canEdit),
    [hasRecipient, refetch, canEdit]
  );

  if (!rows.length && !paginatedTableProps?.loading) {
    return (
      <>
        <SmallTableEmptyState
          fill={emptyStateFill}
          icon={<Wallet01Icon size={32} color={COLORS.TEXT.PRIMARY} />}
          heading="No cash flows"
          description="Add income and expenses for this client; only waterfall projections will be impacted"
          action={
            canEdit ? (
              <Button
                variant="primary"
                size="sm"
                startIcon={PlusIcon}
                onClick={() => setIsAddCashflowDrawerOpen(true)}
              >
                Add cash flow
              </Button>
            ) : undefined
          }
        />
        <CashflowDrawer
          isOpen={isAddCashflowDrawerOpen}
          onClose={() => setIsAddCashflowDrawerOpen(false)}
          cashflowId={null}
        />
      </>
    );
  }

  return (
    <>
      <CashflowDrawer
        isOpen={isModalOpen}
        onClose={closeModal}
        cashflowId={cashflowId}
        recipient={individualId || entityId}
      />
      <DataTable
        columns={columns}
        rows={rows}
        onRowClick={onRowClick}
        pageSize={MAXIMUM_ROW_COUNT}
        {...paginatedTableProps}
        hideFooterPagination
      />
    </>
  );
}
